import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, ExternalLink } from "lucide-react";
import type { UserInfo } from "@/types/superAdmin";
import { useTranslation } from "react-i18next";

interface SendResetLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserInfo | null;
  onConfirm: (userId: string) => void;
  isLoading: boolean;
}

export function SendResetLinkDialog({
  open,
  onOpenChange,
  user,
  onConfirm,
  isLoading,
}: SendResetLinkDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const handleConfirm = () => {
    if (!user) return;
    onConfirm(user.user_id);
  };

  if (!user) return null;

  const userName = `${user.first_name} ${user.last_name}`.trim() || t("sendResetLink.defaultUserName");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            {t("sendResetLink.title")}
          </DialogTitle>
          <DialogDescription>
            {t("sendResetLink.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Info */}
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("sendResetLink.name")}</span>
              <span className="text-sm font-medium">{userName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("sendResetLink.email")}</span>
              <span className="text-sm font-medium">{user.email}</span>
            </div>
          </div>

          {/* Info */}
          <div className="p-3 bg-primary/5 border border-primary/10 rounded-lg">
            <p className="text-sm text-muted-foreground">
              {t("sendResetLink.info")}
            </p>
          </div>

          {/* Redirect URL */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ExternalLink className="h-3 w-3" />
            <span>{t("sendResetLink.redirectTo")} core.stryvfit.app/set-password</span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t("common:cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t("sendResetLink.sending") : t("sendResetLink.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
